import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useLanguage } from "@/hooks/use-language"
import { useAuth } from "@/contexts/AuthContext"
import { useLicense } from "@/hooks/use-license"
import { useNavigate } from "react-router-dom"
import {
  Crown,
  Calendar,
  Zap,
  ArrowRight,
  CheckCircle2,
  XCircle
} from "lucide-react"

export function PlanStatusBanner() {
  const { language } = useLanguage()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { license, licenseType, isActive, isExpired, isTrial, daysUntilExpiry } = useLicense(user?.uid)

  // Usuario sin plan (free)
  if (!license || (!isActive && !isExpired)) {
    return (
      <Card className="border-primary/50 bg-primary/5 shadow-lg shadow-primary/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/30">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {language === "en" ? "Activate Your 7-Day Free Trial" : "Activa tu Prueba Gratuita de 7 Días"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === "en"
                    ? "Free-trial activation is temporarily paused while access controls are being moved to the secure backend."
                    : "La activación de prueba gratuita está pausada temporalmente mientras los controles de acceso se trasladan al backend seguro."}
                </p>
              </div>
            </div>
            <Button
              disabled
              size="lg"
              className="gap-2 whitespace-nowrap"
            >
              {language === "en" ? "Trial Temporarily Unavailable" : "Prueba Temporalmente No Disponible"}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Trial activo
  if (isTrial && isActive && daysUntilExpiry !== null) {
    return (
      <Card className="border-primary/50 bg-primary/5 shadow-lg shadow-primary/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/30">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10">
                    {language === "en" ? "Free Trial" : "Prueba Gratuita"}
                  </Badge>
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-500 bg-emerald-500/10">
                    {daysUntilExpiry} {language === "en" ? "days left" : "días restantes"}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {language === "en" ? "Free Trial Active" : "Prueba Gratuita Activa"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === "en"
                    ? `You're receiving signals in the public Telegram channel. ${daysUntilExpiry} days remaining to explore all features.`
                    : `Estás recibiendo señales en el canal público de Telegram. Te quedan ${daysUntilExpiry} días para explorar todas las funciones.`}
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/payment")}
              variant="outline"
              size="lg"
              className="gap-2 whitespace-nowrap border-primary/50 text-primary hover:bg-primary/10"
            >
              {language === "en" ? "Upgrade to Premium" : "Actualizar a Premium"}
              <Crown className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Trial expirado
  if (isTrial && isExpired) {
    return (
      <Card className="border-destructive/50 bg-destructive/5 shadow-lg shadow-destructive/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0 border border-destructive/30">
                <XCircle className="w-6 h-6 text-destructive" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="border-destructive/30 text-destructive bg-destructive/10">
                    {language === "en" ? "Trial Expired" : "Prueba Expirada"}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {language === "en" ? "Free Trial Ended" : "Prueba Gratuita Finalizada"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === "en"
                    ? "Your free trial has ended. Upgrade to Premium to continue receiving signals and unlock all features."
                    : "Tu prueba gratuita ha finalizado. Actualiza a Premium para continuar recibiendo señales y desbloquear todas las funciones."}
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/payment")}
              size="lg"
              className="gap-2 whitespace-nowrap"
            >
              {language === "en" ? "Upgrade to Premium" : "Adquirir Servicio Premium"}
              <Crown className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Premium activo
  if (licenseType === "premium" && isActive) {
    return (
      <Card className="border-primary/50 bg-gradient-to-r from-primary/10 to-secondary/10 shadow-lg shadow-primary/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0 border border-primary/30 shadow-lg">
                <Crown className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0">
                    {language === "en" ? "Premium" : "Premium"}
                  </Badge>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {language === "en" ? "Premium Active" : "Premium Activo"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === "en"
                    ? "You have access to all premium features including private Telegram channel and unlimited signal history."
                    : "Tienes acceso a todas las funciones premium incluyendo el canal privado de Telegram e historial ilimitado de señales."}
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/settings")}
              variant="outline"
              size="lg"
              className="gap-2 whitespace-nowrap border-primary/50 text-primary hover:bg-primary/10"
            >
              {language === "en" ? "Manage Plan" : "Gestionar Plan"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return null
}

